import React, { useMemo } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    Stack,
    Typography,
} from "@mui/material";
import type { SvgIconComponent } from "@mui/icons-material";
import {
    AirlineSeatReclineNormal as CabInteriorIcon,
    CarRepair as BrakeIcon,
    Checklist as ChecklistFallbackIcon,
    Description as DocumentsIcon,
    DirectionsCar as ExteriorIcon,
    ExpandMore as ExpandMoreIcon,
    MeetingRoom as InteriorIcon,
    RvHookup as TrailerStructureIcon,
    Speed as AirBrakeIcon,
    TripOrigin as TiresIcon,
} from "@mui/icons-material";
import type { PtiChecklistAnswerValue } from "../../../api/ptiApi";
import {
    formatChecklistAnswer,
    type PtiChecklistQuestion,
} from "../ptiChecklist";
import {
    PTI_CHECKLIST_ANSWER_NA,
    PTI_CHECKLIST_SECTIONS,
} from "../ptiConstants";

interface PtiChecklistPanelProps {
    questions: PtiChecklistQuestion[];
    answers: Record<string, PtiChecklistAnswerValue>;
}

const SECTION_ICONS: Record<string, SvgIconComponent> = {
    [PTI_CHECKLIST_SECTIONS.ExteriorCheck]: ExteriorIcon,
    [PTI_CHECKLIST_SECTIONS.BrakeSystemCheck]: BrakeIcon,
    [PTI_CHECKLIST_SECTIONS.CabInteriorCheck]: CabInteriorIcon,
    [PTI_CHECKLIST_SECTIONS.DocumentsCheck]: DocumentsIcon,
    [PTI_CHECKLIST_SECTIONS.AirBrakeTest]: AirBrakeIcon,
    [PTI_CHECKLIST_SECTIONS.ExteriorAndStructure]: TrailerStructureIcon,
    [PTI_CHECKLIST_SECTIONS.AirAndTires]: TiresIcon,
    [PTI_CHECKLIST_SECTIONS.Interior]: InteriorIcon,
};

function getSectionIcon(title: string): SvgIconComponent {
    return SECTION_ICONS[title] ?? ChecklistFallbackIcon;
}

function getAnswerValue(
    answer: PtiChecklistAnswerValue | undefined
): boolean | typeof PTI_CHECKLIST_ANSWER_NA | string | undefined {
    if (answer === undefined || answer === null) {
        return undefined;
    }

    if (typeof answer === "object" && "value" in answer) {
        return answer.value;
    }

    return answer;
}

function getAnswerChipColor(
    answer: PtiChecklistAnswerValue | undefined
): "success" | "error" | "default" | undefined {
    const value = getAnswerValue(answer);

    if (value === true) {
        return "success";
    }
    if (value === false) {
        return "error";
    }
    if (value === PTI_CHECKLIST_ANSWER_NA) {
        return "default";
    }

    return undefined;
}

function getAnswerChipLabel(
    answer: PtiChecklistAnswerValue | undefined,
    question?: PtiChecklistQuestion
): string | null {
    const value = getAnswerValue(answer);

    if (value === undefined || value === null) {
        return null;
    }

    if (value === true) {
        return question?.questionType === "PassFail" ? "Pass" : "Y";
    }
    if (value === false) {
        return question?.questionType === "PassFail" ? "Fail" : "N";
    }
    if (value === PTI_CHECKLIST_ANSWER_NA) {
        return "N/A";
    }

    return null;
}

function getAnswerNote(answer: PtiChecklistAnswerValue | undefined): string | null {
    if (typeof answer === "object" && answer !== null && "note" in answer) {
        const note = answer.note?.trim();
        return note || null;
    }

    return null;
}

const PtiChecklistPanel: React.FC<PtiChecklistPanelProps> = ({ questions, answers }) => {
    const sections = useMemo(() => {
        const grouped = new Map<string, PtiChecklistQuestion[]>();

        questions.forEach((question) => {
            const section = question.section?.trim() || "Other";
            const existing = grouped.get(section);
            if (existing) {
                existing.push(question);
            } else {
                grouped.set(section, [question]);
            }
        });

        return Array.from(grouped.entries()).map(([section, sectionQuestions]) => ({
            section,
            questions: sectionQuestions,
            answeredCount: sectionQuestions.filter(
                (question) => getAnswerValue(answers[question.key]) !== undefined
            ).length,
        }));
    }, [questions, answers]);

    if (questions.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No checklist answers were provided.
            </Typography>
        );
    }

    return (
        <Box
            sx={{
                display: "grid",
                gap: 1.5,
                alignItems: "start",
                gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                    xl: "repeat(3, minmax(0, 1fr))",
                },
            }}
        >
            {sections.map(({ section, questions: sectionQuestions, answeredCount }) => {
                const SectionIcon = getSectionIcon(section);

                return (
                    <Accordion
                        key={section}
                        disableGutters
                        elevation={0}
                        defaultExpanded
                        sx={{
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            minWidth: 0,
                            bgcolor: "background.paper",
                            "&:before": { display: "none" },
                            "&.Mui-expanded": { margin: 0 },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: 48,
                                px: 1.5,
                                color: "text.primary",
                                "& .MuiAccordionSummary-content": {
                                    my: 1,
                                    alignItems: "center",
                                    gap: 1,
                                    color: "text.primary",
                                },
                                "& .MuiAccordionSummary-expandIconWrapper": {
                                    color: "text.secondary",
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 0.75,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: (theme) => `${theme.palette.primary.main}18`,
                                    flexShrink: 0,
                                }}
                            >
                                <SectionIcon color="primary" sx={{ fontSize: 18 }} />
                            </Box>
                            <Typography
                                variant="subtitle2"
                                fontWeight={700}
                                color="text.primary"
                                sx={{ flex: 1, lineHeight: 1.3 }}
                            >
                                {section}
                            </Typography>
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`${answeredCount}/${sectionQuestions.length}`}
                                sx={{ height: 22, fontSize: 12 }}
                            />
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                            <Stack spacing={0}>
                                {sectionQuestions.map((question) => {
                                    const answer = answers[question.key];
                                    const chipLabel = getAnswerChipLabel(answer, question);
                                    const chipColor = getAnswerChipColor(answer);
                                    const note = getAnswerNote(answer);

                                    return (
                                        <Stack
                                            key={question.key}
                                            direction="row"
                                            spacing={1.5}
                                            alignItems="flex-start"
                                            sx={{
                                                py: 0.75,
                                                borderBottom: 1,
                                                borderColor: "divider",
                                                "&:last-of-type": { borderBottom: 0 },
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.primary"
                                                sx={{ flex: 1, lineHeight: 1.4, pt: 0.25 }}
                                            >
                                                {question.label}
                                            </Typography>
                                            <Stack
                                                spacing={0.5}
                                                alignItems="flex-end"
                                                sx={{ flexShrink: 0 }}
                                            >
                                                {chipLabel ? (
                                                    <Chip
                                                        size="small"
                                                        label={chipLabel}
                                                        color={chipColor}
                                                        variant={chipColor ? "filled" : "outlined"}
                                                        sx={{
                                                            height: 22,
                                                            minWidth: 36,
                                                            fontSize: 12,
                                                            fontWeight: 600,
                                                            "& .MuiChip-label": { px: 1 },
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {formatChecklistAnswer(question, answer)}
                                                    </Typography>
                                                )}
                                                {note ? (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ textAlign: "right", maxWidth: 220 }}
                                                    >
                                                        {note}
                                                    </Typography>
                                                ) : null}
                                            </Stack>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
};

export default PtiChecklistPanel;
